import React, { useRef } from "react";
import { EyeIcon, FileIcon, FileSearchIcon, Link2Icon, MailIcon, MessageSquareIcon, MoreHorizontalIcon, UploadIcon, BoxIcon } from "lucide-react";
import { InlinePopover } from "./InlinePopover";
import { documentCount } from "../../utils/conditions";
import { Condition } from "../../types/conditions";
interface RowActionsProps {
  condition: Condition;
  onToggleEmail: () => void;
  onToggleBorrowerVisible: () => void;
  onUpload: () => void;
  onLinkDocuments: () => void;
  onComments: () => void;
}
const iconBtn = 'relative inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';
const offTone = 'text-slate-400 hover:bg-slate-100 hover:text-slate-700';
const onTone = 'bg-brand/10 text-brand hover:bg-brand/15';
export function RowActions({
  condition,
  onToggleEmail,
  onToggleBorrowerVisible,
  onUpload,
  onLinkDocuments,
  onComments
}: RowActionsProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const docs = documentCount(condition);
  const comments = condition.comments.length;
  return <div className="flex items-center justify-end gap-0.5" data-id="element-236" data-mp="12wnorih8xt6k">
      <input ref={fileRef} type="file" className="sr-only" tabIndex={-1} aria-hidden="true" onChange={e => {
      if (e.target.files?.length) onUpload();
      e.target.value = '';
    }} data-id="element-237" data-mp="p6akbq3kcwlz" />

      <Tooltip label={condition.includeInEmail ? 'Included in email' : 'Include in email'} data-id="element-238" data-mp="f84fazmgnacw">
        <button type="button" onClick={onToggleEmail} aria-pressed={condition.includeInEmail} aria-label="Include in email" className={`${iconBtn} ${condition.includeInEmail ? onTone : offTone}`} data-id="element-239" data-mp="fah704i0sz35">
          <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-240" data-mp="69su5d4d54ru" />
        </button>
      </Tooltip>

      <Tooltip label={condition.borrowerVisible ? 'Borrower can see' : 'Hidden from borrower'} data-id="element-241" data-mp="ab4ccxkwqnq0">
        <button type="button" onClick={onToggleBorrowerVisible} aria-pressed={condition.borrowerVisible} aria-label="Can borrower see" className={`${iconBtn} ${condition.borrowerVisible ? onTone : offTone}`} data-id="element-242" data-mp="20jcvg5mw8tyr">
          <EyeIcon className="h-4 w-4" aria-hidden="true" data-id="element-243" data-mp="3dhmyxhv5vwrc" />
        </button>
      </Tooltip>

      <Tooltip label="Documents" data-id="element-244" data-mp="38n987srg2dt0">
        <InlinePopover ariaLabel={`Documents for ${condition.name}${docs ? ` (${docs})` : ''}`} align="right" triggerClassName={`${iconBtn} ${offTone}`} trigger={<>
              <FileIcon className="h-4 w-4" aria-hidden="true" data-id="element-246" data-mp="2zmkqoo2udvbd" />
              <Badge count={docs} data-id="element-247" data-mp="4l8tqtqzpkn" />
            </>} data-id="element-245" data-mp="34it3vvxhbefw">
          {close => <div role="menu" className="w-64 rounded-md border border-slate-200 bg-white py-1 shadow-lg" data-id="element-248" data-mp="3s8dpkml0opsv">
              <MenuItem icon={FileSearchIcon} title={`Review documents${docs ? ` (${docs})` : ''}`} hint={docs ? 'Open attached files' : 'Nothing attached yet'} disabled={docs === 0} onClick={close} data-id="element-249" data-mp="3nhdsnhbwe7qd" />
              <div className="my-1 border-t border-slate-100" data-id="element-250" data-mp="jtc2bbwt8zyx" />
              <MenuItem icon={UploadIcon} title="Upload new file" hint="From your computer" onClick={() => {
            close();
            fileRef.current?.click();
          }} data-id="element-251" data-mp="f2dl8a3zzxum" />
              <MenuItem icon={Link2Icon} title="Link existing document" hint="Already in the loan file" onClick={() => {
            close();
            onLinkDocuments();
          }} data-id="element-252" data-mp="mfmm8zwllfw4" />
            </div>}
        </InlinePopover>
      </Tooltip>

      <Tooltip label="Comments" data-id="element-253" data-mp="10dt1szxoit6z">
        <button type="button" onClick={onComments} aria-label={`Comments${comments ? ` (${comments})` : ''}`} className={`${iconBtn} ${offTone}`} data-id="element-254" data-mp="10eundycbmy2y">
          <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-255" data-mp="1ab7ecoghexu0" />
          <Badge count={comments} data-id="element-256" data-mp="1a9e6x6y1o9ky" />
        </button>
      </Tooltip>

      <Tooltip label="Condition history" data-id="element-257" data-mp="1f3iffumykpo5">
        <button type="button" aria-label="Condition history" className={`${iconBtn} ${offTone}`} data-id="element-258" data-mp="1f4jsu5rg5o91">
          <MoreHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-259" data-mp="1k33d81qe6rza" />
        </button>
      </Tooltip>
    </div>;
}
function Badge({
  count
}: {
  count: number;
}) {
  if (!count) return null;
  return <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-brand px-1 text-[10px] font-bold leading-4 text-white ring-2 ring-white" data-id="element-260" data-mp="1szm3t7b5i3sp">
      {count}
    </span>;
}
function Tooltip({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div className="group/action relative" data-id="element-261" data-mp="1p49l8ba5ek7n">
      {children}
      <span role="tooltip" className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover/action:opacity-100" data-id="element-262" data-mp="1k6zp6cs97aau">
        {label}
      </span>
    </div>;
}
interface MenuItemProps {
  icon: BoxIcon;
  title: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
}
function MenuItem({
  icon: Icon,
  title,
  hint,
  onClick,
  disabled
}: MenuItemProps) {
  return <button type="button" role="menuitem" onClick={onClick} disabled={disabled} className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent" data-id="element-263" data-mp="2hs5hxfzpwc4f">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" data-id="element-264" data-mp="3n0h4h336wsj" />
      <span data-id="element-265" data-mp="3ls3qie035uyr">
        <span className="block text-sm font-medium text-slate-800" data-id="element-266" data-mp="3lslyg7w41xmi">{title}</span>
        <span className="block text-xs text-slate-500" data-id="element-267" data-mp="3qq5cb6owc8dx">{hint}</span>
      </span>
    </button>;
}